import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
	onGetUser,
	onUpdateUser,
	onGetCategories,
	onSendProfileConfig,
	onGetProfileConfig,
	onListenPartnerInvitation,
	onGetReferralCode,
} from "./OnboardingThunk";
import User from "../../domain/entities/getUser";
import Category from "../../domain/entities/category";
import UserInfo from "../../domain/entities/user";
import ProfileConfig from "../../domain/entities/profileConfig";
import GetProfileConfigInfo from "../../domain/entities/getProfileConfigInfo";
import { Partner } from "../../../settings/domain/entities/Partner";
import { ReferralCode } from "../../domain/entities/referralCode";
import {
	syncLocationStateFromUser,
} from "../../../../core/utilities/locationStorage";

interface OnboardingState {
	isLoading: boolean;
	userLoading: boolean;
	isUpdateLoading: boolean;
	error?: string;
	user?: User;
	userInfo?: UserInfo;
	profileConfig?: ProfileConfig;
	showMeList: Category[];
	desiresList: Category[];
	sexualOrientationList: Category[];
	genderList: Category[];
	maritialList: Category[];
	getProfileConfig?: GetProfileConfigInfo;
	referralCode?: ReferralCode;
	partner?: Partner | null;
	pushState?: string | null;
}

const initialState: OnboardingState = {
	isLoading: false,
	userLoading: false,
	isUpdateLoading: false,
	error: undefined,
	user: undefined,
	userInfo: undefined,
	showMeList: [],
	desiresList: [],
	sexualOrientationList: [],
	genderList: [],
	maritialList: [],
	profileConfig: undefined,
	referralCode: undefined,
	getProfileConfig: undefined,
	
	partner: null,
	pushState:'denied'
};

const onboardingSlice = createSlice({
	name: "onboarding",
	initialState,
	reducers: {
		deleteUserInfo: (state) => {
			state.userInfo = undefined;
		},
		changePushState: (state,action:PayloadAction<string|null>) => {
			state.pushState = action.payload;
		},
		//onAddPartner
		setPartner: (state, action: PayloadAction<Partner>) => {
			if (state.profileConfig) {
				state.profileConfig = {
					...state.profileConfig,
					partner: {
						...state.profileConfig.partner,
						...action.payload
					},
				};
				
			}
			state.partner = {
				...state.partner,
				...action.payload
			}
		},
		deletePartner:(state) =>{
			state.partner = null;
		},
		newPartnerInvited: (state, action: PayloadAction<Partner>) => {
			if (state.partner) {
				state.partner = action.payload;
			}
        },

		setPhone: (state, action: PayloadAction<string>) => {
			if (state.user) {
				state.user = {
					...state.user,
					phone_number: action.payload,
				};
			}
		},
		setLatLng: (state, action: PayloadAction<string[]>) => {
			if (state.user) {
				state.user = {
					...state.user,
					lat: action.payload[0],
					lng: action.payload[1],
				};
			}
		},
		setEmails: (state, action: PayloadAction<string>) => {
			if (state.user) {
				state.user = {
					...state.user,
					email: action.payload,
				};
			}
		},
		setUserInfo: (state, action: PayloadAction<Partial<UserInfo>>) => {
			if (state.userInfo) {
				state.userInfo = {
					...state.userInfo,
					...action.payload,
				};
			} else {
				state.userInfo = action.payload as UserInfo;
			}
		},
		setSavedAboutMe: (state, action: PayloadAction<{ userId: string; bio: string }>) => {
			if (state.user?.id === action.payload.userId) state.user.bio = action.payload.bio;
		},
		setProfileConfig: (
			state,
			action: PayloadAction<Partial<ProfileConfig>>
		) => {
			if (state.profileConfig) {
				state.profileConfig = {
					...state.profileConfig,
					...action.payload,
				};
			} else {
				state.profileConfig = action.payload as ProfileConfig;
			}
		},
		setUpdateGetProfileConfig: (state, action: PayloadAction<string>) => {
			if (state.getProfileConfig) {
				state.getProfileConfig.status_account = action.payload;
			}
		},
		setUpdateCreditsUser: (state, action: PayloadAction<number>) => {
			if (state.user) {
				state.user.messages_count = action.payload;
			}
		},
		
	},
	extraReducers: (builder) => {
		builder
			.addCase(onGetUser.pending, (state) => {
				state.userLoading = true;
			})
			.addCase(onGetUser.fulfilled, (state, action: PayloadAction<User>) => {
				state.userLoading = false;
				state.user = action.payload;
				syncLocationStateFromUser(action.payload);
			})
			.addCase(onGetUser.rejected, (state, action) => {
				state.error = action.error.message;
				state.userLoading = false;
			})
			.addCase(onUpdateUser.pending, (state) => {
				state.isUpdateLoading = true;
			})
			.addCase(onUpdateUser.fulfilled, (state, action: PayloadAction<User>) => {
				state.isUpdateLoading = false;
				state.user = action.payload;
				syncLocationStateFromUser(action.payload);
			})
			.addCase(onUpdateUser.rejected, (state, action) => {
				state.error = action.error.message;
				state.isUpdateLoading = false;
			})
			.addCase(onGetCategories.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				onGetCategories.fulfilled,
				(
					state,
					action: PayloadAction<
						[Category[], Category[], Category[], Category[], Category[]]
					>
				) => {
					state.isLoading = false;
					state.showMeList = action.payload[0];
					state.desiresList = action.payload[1];
					state.sexualOrientationList = action.payload[2];
					state.genderList = action.payload[3];
					state.maritialList = action.payload[4];
				}
			)
			.addCase(onGetCategories.rejected, (state, action) => {
				state.error = action.error.message;
				state.isLoading = false;
			})
			.addCase(onSendProfileConfig.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(
				onSendProfileConfig.fulfilled,
				(state, action: PayloadAction<GetProfileConfigInfo>) => {
					state.isLoading = false;
					state.getProfileConfig = action.payload;
				}
			)
			.addCase(onSendProfileConfig.rejected, (state, action) => {
				state.error = action.error.message;
				state.isLoading = false;
			})
			.addCase(onGetProfileConfig.pending, (state) => {
				state.userLoading = true;
			})
			.addCase(
				onGetProfileConfig.fulfilled,
				(state, action: PayloadAction<GetProfileConfigInfo>) => {
					state.userLoading = false;
					state.getProfileConfig = action.payload;
					state.partner = (action.payload as any).partner || null;
				}
			)
			.addCase(onGetProfileConfig.rejected, (state, action) => {
				state.error = action.error.message;
				state.userLoading = false;
			})

			.addCase(onListenPartnerInvitation.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(onListenPartnerInvitation.fulfilled, (state) => {
                state.isLoading = false;
                state.error = undefined;
            })
            .addCase(onListenPartnerInvitation.rejected, (state, action) => {
                state.error = action.error.message;
                state.isLoading = false;
            })

			.addCase(onGetReferralCode.pending, (state) => {
				state.userLoading = true;
			})
			.addCase(onGetReferralCode.fulfilled, (state, action: PayloadAction<ReferralCode>) => {
					state.userLoading = false;
					state.referralCode = action.payload;
				}
			)
			.addCase(onGetReferralCode.rejected, (state, action) => {
				state.error = action.error.message;
				state.userLoading = false;
			})
		;
	},
});

export const {
	setPhone,
	setEmails,
	setUserInfo,
	setSavedAboutMe,
	setProfileConfig,
	setLatLng,
	setUpdateGetProfileConfig,
	setPartner,
	deletePartner,
	setUpdateCreditsUser,
	deleteUserInfo,
	changePushState
} = onboardingSlice.actions;
export default onboardingSlice.reducer;
